import {
  gtcAddToCart,
} from "gtcUtils";

const GET_CART_DATA = 'getCartData';
const BONUS_CLOSE = 'gtc-opener:closebonus-items';
const BONUS_ITEM_PROPERTY = '_bonus_item';
const BONUS_ITEM_VALUE = 'true';
const BOGO_REJECTED_ATTR = 'bogo_rejected';

let isRejectingGift = false;
let isClearingBogoRejected = false;
let isAutoAddingGift = false;
let isRemovingInvalidBonus = false;
let isSyncingAutoBonus = false;

function isBonusItemProperty(properties) {
  if (!properties) return false;
  const value = properties[BONUS_ITEM_PROPERTY];
  return value === true || value === BONUS_ITEM_VALUE;
}

function isBogoRejected(attributes) {
  if (!attributes) return false;
  const value = attributes[BOGO_REJECTED_ATTR];
  return value === true || value === 'true';
}

function cartHasBonusItem(cart, bonusProductIds) {
  return cart?.items?.some((item) => bonusProductIds.includes(item.product_id)) ?? false;
}

function findAutoAddedBonusCartItem(cart, bonusProductIds) {
  return cart?.items?.find((item) => (
    isBonusItemProperty(item.properties)
    && bonusProductIds.includes(item.product_id)
  )) ?? null;
}

function shouldRemoveAutoBonusGift(data) {
  if (!data || data.bogoRejected) return false;
  if (!data.hasBonusItem) return false;
  if (data.canRunBonusItems === false) return true;
  if (data.thresholdReached === false) return true;
  return false;
}

function shouldAutoAddBonusGift(data) {
  if (!data || data.bogoRejected) return false;
  if (data.canRunBonusItems === false) return false;
  if (!data.autoAddSingleProduct) return false;
  if (!data.autoAddVariantId) return false;
  if (!data.thresholdReached) return false;
  if (data.hasBonusItem) return false;
  return true;
}

function isBonusLineMutationInFlight() {
  return isRejectingGift || isAutoAddingGift || isRemovingInvalidBonus;
}

async function fetchThresholdData() {
  const response = await fetch('/cart?view=gtc-bonus-item-threshold', { cache: 'no-store' });
  if (!response.ok) throw new Error('Failed to fetch threshold data');
  return response.json();
}

async function removeInvalidAutoBonusGiftIfNeeded(thresholdData = null) {
  if (isBonusLineMutationInFlight()) return false;

  isRemovingInvalidBonus = true;

  try {
    const data = thresholdData ?? await fetchThresholdData();
    if (!shouldRemoveAutoBonusGift(data)) return false;

    const bonusProductIds = data.bonusProductIds ?? [];
    if (!bonusProductIds.length) return false;

    const cart = await fetchCart();
    if (isBogoRejected(cart.attributes)) return false;

    const cartItem = findAutoAddedBonusCartItem(cart, bonusProductIds);

    if (!cartItem?.key) return false;

    await changeLineQuantity(cartItem.key, 0);
    const freshCart = await fetchCart();
    refreshThemeCart(freshCart);
    return true;
  } catch (error) {
    console.error('Error removing invalid auto bonus gift:', error);
    return false;
  } finally {
    isRemovingInvalidBonus = false;
  }
}

async function autoAddBonusGiftFromThresholdData(thresholdData) {
  if (isBonusLineMutationInFlight()) return false;
  if (!shouldAutoAddBonusGift(thresholdData)) return false;

  const bonusProductIds = thresholdData.bonusProductIds ?? [];
  if (!bonusProductIds.length) return false;

  isAutoAddingGift = true;

  try {
    const cart = await fetchCart();

    if (isBogoRejected(cart.attributes)) return false;
    if (cartHasBonusItem(cart, bonusProductIds)) return false;
    if (findAutoAddedBonusCartItem(cart, bonusProductIds)) return false;

    await gtcAddToCart({
      variantId: thresholdData.autoAddVariantId,
      properties: { [BONUS_ITEM_PROPERTY]: BONUS_ITEM_VALUE },
      callBack: () => {
        if (window.location.pathname === '/cart') {
          window.location.reload();
        }
      },
    });

    const freshCart = await fetchCart();
    const added = cartHasBonusItem(freshCart, bonusProductIds);

    if (added) {
      refreshThemeCart(freshCart);
    }

    return added;
  } catch (error) {
    console.error('Error auto-adding bonus gift:', error);
    return false;
  } finally {
    isAutoAddingGift = false;
  }
}

async function syncAutoBonusGiftWithMarket(thresholdData = null) {
  if (isSyncingAutoBonus || isRejectingGift) {
    return { removed: false, added: false };
  }

  isSyncingAutoBonus = true;

  try {
    const data = thresholdData ?? await fetchThresholdData();
    const cart = await fetchCart();

    if (isBogoRejected(cart.attributes) || data.bogoRejected) {
      return { removed: false, added: false };
    }

    if (shouldRemoveAutoBonusGift(data)) {
      const removed = await removeInvalidAutoBonusGiftIfNeeded(data);
      return { removed, added: false };
    }

    if (shouldAutoAddBonusGift(data)) {
      const added = await autoAddBonusGiftFromThresholdData(data);
      return { removed: false, added };
    }

    return { removed: false, added: false };
  } catch (error) {
    console.error('Error syncing auto bonus gift with market:', error);
    return { removed: false, added: false };
  } finally {
    isSyncingAutoBonus = false;
  }
}

function syncBonusItemPresent(settings) {
  settings.hasBonusItem = true;
  settings.showBonusItems = false;
}

function shouldRunBonusItems(settings) {
  if (!settings) return false;
  if (settings.bogoRejected) return false;
  if (settings.canRunBonusItems === false) return false;
  if (!settings.bonusItems?.length) return false;
  if ((settings.threshold ?? 0) <= 0) return false;
  return true;
}

function disableBonusItemsHost(host) {
  if (!host) return;
  host.classList.add('hidden');
  document.dispatchEvent(new CustomEvent('gtc-bonus-items:disabled'));
  document.dispatchEvent(new CustomEvent(BONUS_CLOSE));
}

async function fetchCart() {
  const response = await fetch('/cart.js', { cache: 'no-store' });
  if (!response.ok) throw new Error('Failed to fetch cart');
  return response.json();
}

async function updateCartAttribute(value) {
  const response = await fetch('/cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ attributes: { [BOGO_REJECTED_ATTR]: value } }),
  });

  if (!response.ok) throw new Error('Failed to update cart attribute');
  return response.json();
}

async function changeLineQuantity(lineKey, quantity) {
  const response = await fetch('/cart/change.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ id: lineKey, quantity }),
  });

  if (!response.ok) throw new Error('Failed to change line quantity');
  return response.json();
}

function refreshThemeCart(cart, options = {}) {
  document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
    bubbles: true,
    detail: { cart },
  }));

  document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
    bubbles: true,
    detail: {
      cart,
      openMiniCart: options.openMiniCart ?? false,
    },
  }));

  document.dispatchEvent(new CustomEvent(GET_CART_DATA));
}

function setRejectButtonsDisabled(lineKey, disabled) {
  if (!lineKey) return;

  document.querySelectorAll(`.gtc-bonus-item-reject[data-line-key="${lineKey}"]`).forEach((btn) => {
    btn.disabled = disabled;
  });
}

async function rejectBonusGift(lineKey) {
  if (!lineKey || isRejectingGift) return;

  isRejectingGift = true;
  setRejectButtonsDisabled(lineKey, true);

  try {
    await updateCartAttribute('true');
    await changeLineQuantity(lineKey, 0);
    const cart = await fetchCart();
    refreshThemeCart(cart, { openMiniCart: true });
  } catch (error) {
    console.error('Error rejecting bonus gift:', error);
    setRejectButtonsDisabled(lineKey, false);
  } finally {
    isRejectingGift = false;
  }
}

async function clearBogoRejectedIfEmpty(cart) {
  if (isClearingBogoRejected || !cart) return;
  if (!isBogoRejected(cart.attributes) || cart.item_count !== 0) return;

  isClearingBogoRejected = true;

  try {
    await updateCartAttribute('');
    const freshCart = await fetchCart();
    refreshThemeCart(freshCart);
  } catch (error) {
    console.error('Error clearing bogo_rejected:', error);
  } finally {
    isClearingBogoRejected = false;
  }
}

async function handleCartStateChange(event) {
  try {
    const cart = event?.detail?.cart ?? await fetchCart();
    await clearBogoRejectedIfEmpty(cart);
    const { removed, added } = await syncAutoBonusGiftWithMarket();

    if (removed || added) {
      document.dispatchEvent(new CustomEvent(GET_CART_DATA));
    }
  } catch (error) {
    console.error('Error handling cart state change:', error);
  }
}

function shouldInitializeBonusItems(element) {
  const all = [...document.querySelectorAll('gtc-bonus-items')];

  if (all.length <= 1) return true;

  const isCartPage = window.location.pathname === '/cart';
  const preferredView = isCartPage ? 'main-cart' : 'mini-cart';
  const preferred = all.find((el) => el.getAttribute('data-view') === preferredView);

  if (preferred) return element === preferred;

  return element === all[0];
}

class BonusItems extends HTMLElement {
  constructor() {

    super();
    this.getCartData = this.getCartData.bind(this);
    this.onCartRefresh = this.onCartRefresh.bind(this);
    this.initialized = false;
  }

  connectedCallback() {
    this.addEventListeners();

    if (!shouldInitializeBonusItems(this)) {
      this.classList.add('hidden');
      return;
    }

    this.classList.remove('hidden');
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  init() {
    if (!shouldInitializeBonusItems(this)) {
      return;
    }

    this.settings = {
      bonusItems: JSON.parse(this.getAttribute('bonus-items') ?? '[]'),
      threshold: parseInt(this.getAttribute('threshold')) || 0,
      showBonusItems: false,
      thresholdReached: false,
      hasBonusItem: false,
      bogoRejected: false,
      canRunBonusItems: true,
      autoAddSingleProduct: JSON.parse(this.getAttribute('auto-add-single-product')) || false,
      autoAddSingleProductVariantId: JSON.parse(this.getAttribute('auto-add-single-product-variant-id')) || null,
    };

    if (!this.settings.bonusItems.length) {
      console.error('Bonus Items is required');
      this.remove();
      return;
    }

    this.initialized = true;
    this.getCartData();
  }

  async getCartData() {
    if (!this.initialized) return;

    try {
      const response = await fetch('/cart?view=gtc-bonus-item-threshold', { cache: 'no-store' });
      if (!response.ok) throw new Error('Failed to fetch cart data');
      const data = await response.json();
      this.settings.showBonusItems = data.showBonusItems;
      this.settings.thresholdReached = data.thresholdReached;
      this.settings.hasBonusItem = data.hasBonusItem;
      this.settings.bogoRejected = data.bogoRejected === true;
      this.settings.canRunBonusItems = data.canRunBonusItems !== false;
      this.settings.total = data.total ?? 0;
      this.settings.threshold = data.threshold ?? this.settings.threshold;
      this.settings.autoAddSingleProduct = data.autoAddSingleProduct === true;
      this.settings.autoAddSingleProductVariantId = data.autoAddVariantId ?? this.settings.autoAddSingleProductVariantId;

      if (this.settings.bogoRejected) {
        disableBonusItemsHost(this);
        return;
      }

      const { removed, added } = await syncAutoBonusGiftWithMarket(data);

      if (removed || added) {
        if (!shouldRunBonusItems(this.settings)) {
          disableBonusItemsHost(this);
        }
        return;
      }

      if (!shouldRunBonusItems(this.settings)) {
        disableBonusItemsHost(this);
        return;
      }

      this.render();
    } catch (error) {
      console.error('Error fetching cart data:', error);
    }
  }

  updateProgressBar() {
    const showProgressBar = this.getAttribute('show-progress-bar') === 'true';
    const progressBar = this.querySelector('gtc-bonus-items-progress-bar');

    if (!showProgressBar || !progressBar) return;

    const hideProgress = this.settings.thresholdReached && this.settings.hasBonusItem;
    progressBar.classList.toggle('hidden', hideProgress);

    const threshold = this.settings.threshold;
    if (threshold <= 0) return;

    const progress = Math.min(1, this.settings.total / threshold);
    const inner = progressBar.querySelector('.gtc__bonus-items__progress-bar__inner');

    if (inner) {
      inner.style.setProperty('--progress', `${progress * 100}%`);
    }
  }

  updateDrawerAddButtons() {
    const trigger = this.querySelector('.gtc__bonus-items__trigger');
    const drawerId = trigger?.getAttribute('aria-controls');
    const drawer = drawerId ? document.getElementById(drawerId) : null;
    const section = drawer?.querySelector('#bonus-items-section');

    if (!section) return;

    const canAdd = this.settings.thresholdReached && !this.settings.hasBonusItem;

    section.querySelectorAll('[data-bonus-add-action]').forEach((el) => {
      el.classList.toggle('hidden', !canAdd);
    });
  }

  updateVisibility() {
    const trigger = this.querySelector('.gtc__bonus-items__trigger');

    if (trigger) {
      trigger.classList.toggle('hidden', !this.settings.showBonusItems);
    }

    this.updateProgressBar();
    this.updateDrawerAddButtons();

    const showProgressBar = this.getAttribute('show-progress-bar') === 'true';
    const progressBar = this.querySelector('gtc-bonus-items-progress-bar');
    const progressVisible = showProgressBar && progressBar && !progressBar.classList.contains('hidden');
    const triggerVisible = trigger && !trigger.classList.contains('hidden');

    this.classList.toggle('hidden', !triggerVisible && !progressVisible);
  }

  async autoAddBonusGiftIfNeeded() {
    const data = {
      bogoRejected: this.settings.bogoRejected,
      canRunBonusItems: this.settings.canRunBonusItems,
      autoAddSingleProduct: this.settings.autoAddSingleProduct,
      autoAddVariantId: this.settings.autoAddSingleProductVariantId,
      thresholdReached: this.settings.thresholdReached,
      hasBonusItem: this.settings.hasBonusItem,
      bonusProductIds: this.settings.bonusItems,
    };

    const added = await autoAddBonusGiftFromThresholdData(data);

    if (added) {
      syncBonusItemPresent(this.settings);
    }

    return added || this.settings.hasBonusItem;
  }

  async render() {
    if (
      shouldRunBonusItems(this.settings)
      && !isBonusLineMutationInFlight()
      && this.settings.autoAddSingleProduct
      && this.settings.thresholdReached
      && !this.settings.hasBonusItem
    ) {
      await this.autoAddBonusGiftIfNeeded();
    }

    this.updateVisibility();

    const canAddBonusItems = this.settings.thresholdReached && !this.settings.hasBonusItem;
    const eventName = canAddBonusItems ? 'gtc-bonus-items:enabled' : 'gtc-bonus-items:disabled';
    document.dispatchEvent(new CustomEvent(eventName));

    if (!this.settings.showBonusItems) {
      document.dispatchEvent(new CustomEvent(BONUS_CLOSE));
    }
  }

  addEventListeners() {
    window.addEventListener(GET_CART_DATA, this.getCartData);
    document.documentElement.addEventListener('cart:refresh', this.onCartRefresh);
  }

  removeEventListeners() {
    window.removeEventListener(GET_CART_DATA, this.getCartData);
    document.documentElement.removeEventListener('cart:refresh', this.onCartRefresh);
  }

  onCartRefresh() {
    if (!this.initialized) return;

    requestAnimationFrame(() => {
      if (!shouldInitializeBonusItems(this)) {
        this.classList.add('hidden');
        return;
      }

      this.classList.remove('hidden');
      this.getCartData();
    });
  }


}

if (!customElements.get('gtc-bonus-items')) {
  customElements.define('gtc-bonus-items', BonusItems);
}

/****************************** BonusItem ******************************/

class BonusItem extends HTMLElement {
  static get observedAttributes() {
    return ['variant-id'];
  }

  constructor() {
    super();
    this.click = this.click.bind(this);
    this.keydown = this.keydown.bind(this);
    this.disable = this.disable.bind(this);
    this.enable = this.enable.bind(this);
  }

  connectedCallback() {
    this.init();
  }

  disconnectedCallback() {
    this.removeEventListeners();
  }

  init() {
    this.settings = {
      variantId: this.getAttribute('variant-id'),
    };

    this.dom = {
      drawer: this.closest('gtc-opener'),
    };

    if (!this.settings.variantId) {
      console.error('Variant ID is required');
      this.remove();
      return;
    }

    this.addEventListeners();
  }

  addEventListeners() {
    this.addEventListener('click', this.click);
    this.addEventListener('keydown', this.keydown);
    window.addEventListener('gtc-bonus-items:disabled', this.disable);
    window.addEventListener('gtc-bonus-items:enabled', this.enable);
  }

  removeEventListeners() {
    this.removeEventListener('click', this.click);
    window.removeEventListener('gtc-bonus-items:disabled', this.disable);
    window.removeEventListener('gtc-bonus-items:enabled', this.enable);
  }

  async click() {
    this.disabled = true;
    document.dispatchEvent(new CustomEvent(BONUS_CLOSE));
    await gtcAddToCart({
      variantId: this.settings.variantId,
      properties: { [BONUS_ITEM_PROPERTY]: BONUS_ITEM_VALUE },
      callBack: () => {
        document.dispatchEvent(new CustomEvent(GET_CART_DATA));
        if (window.location.pathname === '/cart') {
          window.location.reload();
        }
      }
    });
    this.disabled = false;
  }

  keydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      this.click();
    }
  }

  disable() {
    this.classList.add('disabled');
    this.removeEventListener('click', this.click);
    this.removeEventListener('keydown', this.keydown);
  }

  enable() {
    this.classList.remove('disabled');
    this.addEventListener('click', this.click);
    this.addEventListener('keydown', this.keydown);
  }
}

if (!customElements.get('gtc-bonus-item')) {
  customElements.define('gtc-bonus-item', BonusItem);
}

document.addEventListener('click', (event) => {
  const btn = event.target.closest('.gtc-bonus-item-reject');
  if (!btn) return;

  event.preventDefault();
  rejectBonusGift(btn.dataset.lineKey);
});

document.documentElement.addEventListener('cart:updated', handleCartStateChange);
document.documentElement.addEventListener('cart:refresh', handleCartStateChange);

syncAutoBonusGiftWithMarket();
